import { SplitFactory } from '@splitsoftware/splitio'
import type SplitIO from '@splitsoftware/splitio/types/splitio'
import device from '@/lib/device'
import { VERSION } from '@/lib/defines'
import subscriptionService from '@/services/subscription-service'
import { getUserId, hasValidUserSession } from '@/services/identity-client'
import browser from '@/lib/browser'
import { captureException } from '@sentry/vue'
import pubsub from '@/lib/pubsub'
import { getFlagKeys } from '@/lib/feature-flags'
import { error } from '@/lib/logger'

type CallbackFunction = () => void

export interface SplitConfig {
  isOn: boolean
  config: Record<string, object>
}

/** Class responsible for checking the status of app features */
export class Split {
  private version: string
  private deviceID: string
  private readyResolver!: CallbackFunction
  private splitClientsAllReady: Promise<void>
  private splitDeviceClient?: SplitIO.IBrowserClient
  private splitUserClient?: SplitIO.IBrowserClient
  private splitDeviceClientReady?: Promise<void>
  private splitUserClientReady?: Promise<void>
  private splitSDK?: SplitIO.IBrowserSDK
  /*
   * Ready state of split clients
   */
  public loaded: boolean

  constructor() {
    // Bind to be used as an event listener for logout
    this.destroyUserClient = this.destroyUserClient.bind(this)
    this.loaded = false
    const version = VERSION || ''
    const versionArray = version.split('.')
    let padded = ''

    versionArray.forEach((item) => {
      padded += item.padStart(2, '0')
    })

    this.version = padded

    // Check for and set a device ID if necessary
    device.checkDeviceID()
    this.deviceID = device.deviceID

    this.splitClientsAllReady = new Promise((res) => (this.readyResolver = res))
  }

  /**
   * Returns a promise which will resolve when the split clients are ready.  In a
   * worst case scenario, this promise could hit the split client ready timeout,
   * which defaults to 10 seconds.
   */
  splitClientsReady() {
    return this.splitClientsAllReady
  }

  private setupReadyResolver() {
    // If this is initialized multiple times, create a reference to the old resolver
    // This will ensure any old promise listeners won't get dropped.
    const oldResolver = this.readyResolver

    // Reassign the promise
    this.splitClientsAllReady = new Promise<void>((res) => {
      // Assign the promise resolve to our class variable.
      this.readyResolver = res
    }).then(() => {
      // Any old promise will now resolve when the reassigned splitClientsAllReady
      // is resolved.
      oldResolver()
    })
  }

  /**
   * Set the split environment.  This can be called more than once in cases where
   * a new account is created.
   */
  async initialize() {
    performance.mark('init_split_start')
    this.loaded = false
    this.setupReadyResolver()
    await this.destroyClients()
    this.setupGracefulShutdown()

    // Return initialized promise - resolved won't indicate it's ready, but that we have completed setup
    return this._initializeSplitClients()
  }

  getSplitKey() {
    return window.environment.splitFishPublicKey
  }

  /**
   * Initialize the split clients to be used by treatment calls
   */
  _initializeSplitClients() {
    this.initializeSplitSDK()
    // Once all split client promises are settled, invoke the resolver, so any code
    // waiting on initialization can continue.
    return Promise.allSettled([
      this.initializeDeviceClient(),
      this.initializeUserClient(),
    ]).then(() => {
      this.loaded = true
      this.readyResolver()
      performance.mark('init_split_end')
      performance.measure('init_split', 'init_split_start', 'init_split_end')
    })
  }

  getSDKConfig(): SplitIO.IBrowserSettings {
    const config: SplitIO.IBrowserSettings = {
      core: {
        authorizationKey: this.getSplitKey(),
        key: this.deviceID,
        trafficType: 'device',
      },
    }
    /** Filter flags retrieved to those defined in flag service */
    if (getFlagKeys()) {
      config.sync = config.sync ?? {}
      config.sync.splitFilters = [{ type: 'byName', values: getFlagKeys() }]
    }
    return config
  }

  initializeSplitSDK() {
    this.splitSDK = SplitFactory(this.getSDKConfig())
  }

  async initializeUserClient() {
    const [validSession, userId] = await Promise.all([
      hasValidUserSession(),
      getUserId(),
      subscriptionService.ready
        ? null
        : subscriptionService.syncSubscriptions(),
    ])
    if (validSession && userId) {
      this.splitUserClient = this.splitSDK?.client(userId, 'user')
      this.splitUserClientReady = new Promise((resolve, reject) => {
        if (this.splitUserClient) {
          this.splitUserClient.once(
            this.splitUserClient.Event.SDK_READY,
            () => {
              resolve()
            }
          )

          this.splitUserClient.once(
            this.splitUserClient.Event.SDK_READY_TIMED_OUT,
            () => {
              reject()
            }
          )
        } else {
          captureException(new Error('split_sdk_init_failure'), {
            tags: {
              split: 'user-client',
            },
          })
          reject()
        }
      })
      return this.splitUserClientReady
    }
  }

  initializeDeviceClient() {
    this.splitDeviceClient = this.splitSDK?.client(this.deviceID, 'device')
    this.splitDeviceClientReady = new Promise((resolve, reject) => {
      if (this.splitDeviceClient) {
        this.splitDeviceClient.once(
          this.splitDeviceClient.Event.SDK_READY,
          () => {
            resolve()
          }
        )

        this.splitDeviceClient.once(
          this.splitDeviceClient.Event.SDK_READY_TIMED_OUT,
          () => {
            reject()
          }
        )
      } else {
        captureException(new Error('split_sdk_init_failure'), {
          tags: {
            split: 'device-client',
          },
        })
        reject()
      }
    })
    return this.splitDeviceClientReady
  }

  /**
   * We need to destroy clients when we want to flush the impression queue
   * eg when the window will be closing or a user session ends.
   */
  setupGracefulShutdown() {
    this.teardownGracefulShutdown()

    pubsub.subscribe('logoutRequestCompleted', this.destroyUserClient)
  }

  teardownGracefulShutdown() {
    pubsub.unsubscribe('logoutRequestCompleted', this.destroyUserClient)
  }

  destroyClients() {
    return Promise.allSettled([
      this.destroyUserClient(),
      this.destroyDeviceClient(),
    ])
  }

  async destroyUserClient() {
    await this.splitUserClient?.destroy()
    this.splitUserClient = undefined
  }

  async destroyDeviceClient() {
    await this.splitDeviceClient?.destroy()
    this.splitDeviceClient = undefined
  }

  getSplits() {
    return this.splitSDK?.manager().splits() ?? []
  }

  getSplit(name: string) {
    return this.splitSDK?.manager().split(name)
  }

  /**
   * Get a treatment for a feature.  If a config is requested, that config object
   * will be returned.  If the treatment is in "on"/"off"/"control", a boolean
   * will be returned.  If not, the direct treatment string will be returned.
   *
   * @see splitClientsReady() - The split client needs to be initialized and ready
   * for getTreatment to return the correct treatment. If the client is not
   * yet initialized the getTreatment call will not fail but return the value
   * "control" (https://help.split.io/hc/en-us/articles/360020528072-Control-treatment)
   */
  getTreatment(
    feature: string,
    withoutConfig?: false,
    customAttributes?: object
  ): string | boolean
  getTreatment(
    feature: string,
    withConfig: true,
    customAttributes?: object
  ): SplitIO.TreatmentWithConfig | boolean | string
  getTreatment(
    feature: string,
    config?: boolean,
    customAttributes?: object
  ): string | boolean | SplitIO.TreatmentWithConfig {
    const split = this.getSplit(feature)
    // feature does not exist in split
    if (split === null) {
      return false
    }
    // split is not initialized yet
    if (split === undefined) {
      /* eslint-disable-next-line */
      console.error(
        `Attempting to get treatment for "${feature}" but split is not initialized`
      )
      return false
    }
    const defaultTreatment = split.defaultTreatment
    let client: SplitIO.IBrowserClient | undefined
    let membership = {}
    let campaign = ''

    const referralData = localStorage.getItem('referral_data')
    if (referralData) {
      try {
        const parsedReferralData = JSON.parse(referralData)
        campaign = parsedReferralData.referral_campaign_id
      } catch {
        // do nothing if fails
      }
    }

    if (split.trafficType === 'user') {
      client = this.splitUserClient
      try {
        const subscription = subscriptionService.getSubscriptionsForApp()
        const memLevel = subscription?.membership_level ?? 'basic'
        const isTrial =
          memLevel === 'trial' || subscription?.is_in_intro_offer_period
        const trialLevel = subscriptionService.getTrialLevel()
        membership = {
          membership: isTrial ? trialLevel : memLevel,
          referred: subscription?.referred ?? false,
          setupSelection: undefined,
        }
      } catch (e) {
        error(
          'Feature Flag Service',
          `Attempting to check memberships in Split user client but subscription service errored when evaluating ${feature}`,
          e
        )
        return false
      }
    } else if (split.trafficType === 'device') {
      client = this.splitDeviceClient
    }

    if (!client) {
      error(
        'Feature Flag Service',
        `No applicable client found - is split initialized? Returning default treatment for ${feature}`
      )
      return this._parseTreatment(defaultTreatment)
    }

    const attributes = {
      platform: 'web',
      mobile_web: browser.isMobile(),
      appVersion: this.version,
      ...membership,
      campaign,
      ...customAttributes,
    }

    try {
      if (config) {
        return client.getTreatmentWithConfig(feature, attributes)
      }
      const treatment = client.getTreatment(feature, attributes)
      return this._parseTreatment(treatment)
    } catch {
      return this._parseTreatment(defaultTreatment)
    }
  }

  /**
   * If the treatment is in "on"/"off"/"control", a boolean will be returned.
   * If not, the direct treatment string will be returned.
   */
  _parseTreatment(treatment: string) {
    if (treatment === 'on' || treatment === 'control' || treatment === 'off') {
      return treatment === 'on'
    }

    return treatment
  }

  /**
   * Get the split data with additional JSON object config.
   *
   * @param key Split flag
   * @param customAttributes Key-value store with any custom attribute to be included with the split config
   */
  getSplitConfigByKey(key: string, customAttributes?: object) {
    const split: SplitConfig = {
      isOn: false,
      config: {},
    }
    const feature = this.getTreatment(key, true, customAttributes)
    if (
      feature &&
      typeof feature === 'object' &&
      feature.treatment !== 'control' &&
      feature.treatment !== 'off'
    ) {
      // checking for multivariant treatments
      split.isOn = true

      if (feature.config) {
        split.config = JSON.parse(feature.config)
      }
    }
    return split
  }
}

// Readonly prevents overwriting class properties, namely the `loaded` state
const split: Readonly<Split> = new Split()
export default split
